import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { getDatabase, ref as dbRef, get, set } from "firebase/database";
import { authStore } from "@/store/auth";
import { buildTokenRecord } from "@/composables/pushTokenRecord.js";

// FCM token registration + rotation for native apps. The account-page toggle
// is the soft ask: only flipping a category on calls registerDevice(), which
// triggers the native permission prompt (never on first app launch).
export function usePushNotifications() {
  const isSupported = () => Capacitor.isNativePlatform();

  const saveToken = async (token) => {
    const userId = authStore.getUserId();
    if (!userId || !token) return;
    const db = getDatabase();
    const tokenRef = dbRef(db, `notifications/tokens/${userId}/${token}`);
    const existing = await get(tokenRef);
    const nowSeconds = Math.floor(Date.now() / 1000);
    await set(
      tokenRef,
      buildTokenRecord(existing.exists() ? existing.val() : null, Capacitor.getPlatform(), nowSeconds)
    );
  };

  // Permission prompt + token write. Returns { granted } so callers can show
  // a "notifications are blocked" hint when the user declines.
  const registerDevice = async () => {
    if (!isSupported()) return { granted: false };
    const permission = await FirebaseMessaging.requestPermissions();
    if (permission.receive !== "granted") return { granted: false };
    const { token } = await FirebaseMessaging.getToken();
    await saveToken(token);
    return { granted: true };
  };

  // On app start: if permission was already granted earlier, refresh the token
  // record silently (handles reinstalls/rotation). Never prompts.
  const refreshTokenIfPermitted = async () => {
    if (!isSupported()) return;
    const permission = await FirebaseMessaging.checkPermissions();
    if (permission.receive !== "granted") return;
    const { token } = await FirebaseMessaging.getToken();
    await saveToken(token);
  };

  const listenForTokenRotation = () => {
    if (!isSupported()) return;
    FirebaseMessaging.addListener("tokenReceived", ({ token }) => {
      saveToken(token).catch((e) => console.error("FCM token save failed:", e));
    });
  };

  // Disassociate this device's token from the current user on logout so the
  // next person to sign in on this device does not inherit their nudges.
  // Called before auth is torn down, while getUserId() still resolves.
  const deleteToken = async () => {
    if (!isSupported()) return;
    const userId = authStore.getUserId();
    if (!userId) return;
    try {
      const { token } = await FirebaseMessaging.getToken();
      if (!token) return;
      const db = getDatabase();
      await set(dbRef(db, `notifications/tokens/${userId}/${token}`), null);
    } catch (e) {
      console.error("FCM token delete failed:", e);
    }
  };

  return { isSupported, registerDevice, refreshTokenIfPermitted, listenForTokenRotation, deleteToken };
}
